import Create from "diagram-js/lib/features/create/Create";
import ElementFactory from "bpmn-js/lib/features/modeling/ElementFactory";
import EventBus from "diagram-js/lib/core/EventBus";
import Palette from "diagram-js/lib/features/palette/Palette";
import PaletteProvider from "bpmn-js/lib/features/palette/PaletteProvider";
import { PaletteEntries } from "diagram-js/lib/features/palette/PaletteProvider";
import Translate from "diagram-js/lib/i18n/translate/translate";
import { assign } from "min-dash";

/**
 * A plugin for hiding specific palette entries in bpmn-js
 */
export default class StekzPaletteProvider implements PaletteProvider {
    static $inject = [
        "palette",
        "eventBus",
        "translate",
        "elementFactory",
        "create",
    ];

    // Array of elements to hide
    static hiddenElements: string[] = [
        "create.subprocess-expanded",
        "create.participant-expanded",
    ];

    constructor(
        palette: Palette,
        eventBus: EventBus,
        private translate: typeof Translate,
        private elementFactory: ElementFactory,
        private create: Create
    ) {
        // Register as palette provider
        palette.registerProvider(this);

        // Intercept the default palette provider.
        eventBus.on<{ providers: PaletteProvider[] }>(
            "palette.getProviders",
            ({ providers }) => {
                // Find the default provider
                const defaultProvider = providers.find(
                    (provider: PaletteProvider) =>
                        provider instanceof PaletteProvider
                );

                if (defaultProvider) {
                    // Store the original getPaletteEntries method
                    const originalGetPaletteEntries =
                        defaultProvider.getPaletteEntries;

                    // Override the method to filter out hidden elements
                    defaultProvider.getPaletteEntries = () => {
                        const entries =
                            originalGetPaletteEntries.call(defaultProvider);
                        // Remove hidden entries
                        StekzPaletteProvider.hiddenElements.forEach(
                            (elementType) => {
                                delete entries[elementType];
                            }
                        );

                        return entries;
                    };
                }
            }
        );
    }

    /**
     * Returns additional palette entries.
     * But we don't have any.
     */
    getPaletteEntries(): PaletteEntries {
        const create = this.create;

        const createAction = (
            type: string,
            group: string,
            className: string,
            title: string,
            options?: object
        ) => {
            const createListener = (event: Event) => {
                var shape = this.elementFactory.createShape(
                    assign({ type: type }, options)
                );
                create.start(event, shape);
            };

            return {
                group: group,
                className: className,
                title: title,
                action: {
                    dragstart: createListener,
                    click: createListener,
                },
            };
        };

        const createParticipant = (event: Event) => {
            create.start(
                event,
                this.elementFactory.createParticipantShape({
                    isExpanded: false,
                })
            );
        };

        return {
            "create.participant-collapsed": {
                group: "collaboration",
                className: "bpmn-icon-participant",
                title: this.translate("Create participant"),
                action: {
                    dragstart: createParticipant,
                    click: createParticipant,
                },
            },
            "create.call-activity": createAction(
                "bpmn:CallActivity",
                "activity",
                "bpmn-icon-call-activity",
                this.translate("Call Activity")
            ),
        };
    }
}
